// Major
import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Container } from "@mui/material";

// Components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import HeaderContainer from "styles/components/headerContainer";
import SliderNavigation from "components/SliderNavigation";

// Styles
import { ImageContainer } from "styles/components/imageContainer";
import ThreeStackCard from "styles/components/threeStackCard";
import theme from "assets/theme";
import { StyledSectionContainerWhiteWrapper, StyledSectionContainer } from "styles/components/SectionContainer/index.styles";
import {
  StyledScrollContainer,
  StyledCardWrapper,
  StyledTitleOverlay,
  StyledTitleText,
  StyledLeftGradient,
  StyledRightGradient,
} from "./index.styles";

const ResourcesSlider = ({ ...props }) => {
  const { cards, title, titleCss, highlightText } = props;

  const ScrollCard = () => {
    const scrollRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(true);

    const cardWidth = 295;

    const topComponent = (item) => {
      return (
        <Container
          sx={{
            padding: "0rem !important",
            margin: "0rem !important",
            position: "relative",
          }}
        >
          <ImageContainer
            image={item.image}
            alt={item.alt}
            cssProps={{ minHeight: { xs: "175px", md: "215px" }, maxWidth: "318px", maxHeight: "215px" }}
          />

          {/* SEO-friendly H3 title overlaid on image */}
          <StyledTitleOverlay>
            <StyledTitleText variant="h5" component="h3">
              {item.title || item.text}
            </StyledTitleText>
          </StyledTitleOverlay>
        </Container>
      );
    };

    const bottomComponent = (item) => {
      return (
        <MKBox>
          <MKButton
            variant="contained"
            color="secondary"
            sx={{ width: "100%", padding: { xs: "0.5rem", md: "inherit" } }}
            component={Link}
            to={item.link}
            aria-label={`learn more about ${item.text}`}
          >
            Learn About {item.text}
          </MKButton>
        </MKBox>
      );
    };

    const scroll = (direction) => {
      if (scrollRef.current) {
        const container = scrollRef.current;
        const scrollAmount = cardWidth * 1;

        if (direction === "left") {
          container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else {
          container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
      }
    };

    const scrollSelect = (newIndex) => {
      if (scrollRef.current && newIndex !== currentIndex) {
        const indexDiff = Math.abs(newIndex - currentIndex);

        const container = scrollRef.current;
        const scrollAmount = cardWidth * indexDiff;

        if (newIndex < currentIndex) {
          container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else {
          container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
      }
    };

    const updateButtonVisibility = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setShowLeftButton(scrollLeft > 0);
        setShowRightButton(scrollLeft < scrollWidth - clientWidth - 10);

        const newIndex = Math.round(scrollLeft / (cardWidth * 2));
        setCurrentIndex(Math.max(0, Math.min(newIndex, Math.ceil(cards.length / 2) - 1)));
      }
    };

    useEffect(() => {
      const container = scrollRef.current;
      if (container) {
        container.addEventListener("scroll", updateButtonVisibility);

        updateButtonVisibility();

        return () => {
          container.removeEventListener("scroll", updateButtonVisibility);
        };
      }
    }, []);

    return (
      <MKBox sx={{ position: "relative" }}>
        {/* Scrollable Container */}
        <MKBox sx={{ position: "relative" }}>
          <StyledScrollContainer ref={scrollRef}>
            <MKBox sx={{ width: { xs: "1rem", isMobile: "2rem", sm: "1.6rem", md: "0rem" }, flexShrink: 0 }} role="presentation" aria-hidden="true" />

            {cards.map((card, index) => (
              <StyledCardWrapper key={index}>
                <ThreeStackCard topComponent={topComponent(card)} midComponent={null} botComponent={bottomComponent(card)} />
              </StyledCardWrapper>
            ))}

            <MKBox sx={{ width: "1.6rem", flexShrink: 0 }} role="presentation" aria-hidden="true" />
          </StyledScrollContainer>
          <StyledLeftGradient />
          <StyledRightGradient />
        </MKBox>

        <MKBox
          sx={{
            textAlign: "center",
            marginTop: "1rem",
          }}
        >
          <MKTypography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
            }}
          >
            ← Swipe to see more resources →
          </MKTypography>
        </MKBox>

        <SliderNavigation
          onSelect={scrollSelect}
          onPrevious={() => scroll("left")}
          onNext={() => scroll("right")}
          currentIndex={currentIndex}
          totalItems={cards.length}
          showLeftButton={showLeftButton}
          showRightButton={showRightButton}
          paginationDots={{
            type: "linear",
            small: false,
            totalDots: Math.ceil(cards.length / 2),
          }}
        />
      </MKBox>
    );
  };

  return (
    <StyledSectionContainerWhiteWrapper id="ketamin-therapist-hub-ketamine-tools">
      <StyledSectionContainer cssProps={{ padding: "2rem 1rem !important" }}>
        <HeaderContainer
          cssProps={titleCss}
          component={"h2"}
          variant={"h2"}
          title={title ? title : "At-Home Ketamine Assisted Therapy Resources"}
          textColor={theme.palette.primary.main}
          highlightText={highlightText ? highlightText : "Resources"}
          highlightColor={theme.palette.accent.main}
        />

        <ScrollCard />
      </StyledSectionContainer>
    </StyledSectionContainerWhiteWrapper>
  );
};

export default ResourcesSlider;
